import { Popover } from 'antd';
import React from 'react';

import { ScaleVisualization } from '~src/interfaces/scale.interface';
import { StoreI } from '~src/rootReducer';
import { translateContent } from '~src/services/translationServices';
import { orderedColorsSets } from '~src/styles/colors';
import { MediaTypes } from '~src/styles/media-templates';

import { connect } from 'react-redux';
import { ButtonShape, ButtonSizes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import DisplayNpsValues from './DisplayNpsValues';
import {
  McqOptionsSubmitButton,
  McqOptionsSubmitButtonContainer,
  ScaleBox,
  ScaleDisplayContainer,
  ScaleMarginContainer,
  StarBtn,
  StarIcon,
  StyledTextBoxWithTooltip
} from './scale.sc';
import ScaleBoxItem from './scaleBoxItem/ScaleBoxItem';
import {TFunction} from "i18next";

interface StateProps {
  media?: MediaTypes;
}

interface DefaultProps {
  labels: Array<{ [key: string]: string }>;
  inContainer: boolean;
  isLast: boolean;
  isPreview: boolean;
}

type Rating = number | number[] | null;

interface OwnProps extends DefaultProps {
  t: TFunction;
  type: ScaleVisualization;
  rating: Rating;
  onRatingSelect?: (key: string, rating: number | number[]) => void;
  saveLoading?: boolean;
  isScaleDetails?: boolean;
  isMultipleChoiceEnabled?: boolean;
}

type Props = StateProps & OwnProps;

interface State {
  hoverRating: number | null;
  selectedRating: Rating;
  preSelected: boolean;
}

class Scale extends React.Component<Props, State> {
  static defaultProps = {
    labels: [],
    inContainer: false,
    isLast: false,
    isPreview: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hoverRating: null,
      selectedRating: null,
      preSelected: props.rating !== null,
    };
  }

  handleMouseOut = () => {
    this.setState({ hoverRating: null });
  };

  handleMouseOver(rating: number) {
    this.setState({ hoverRating: rating });
  }

  handleClick = (rating: number) => {
    if (!this.props.isPreview && !this.props.saveLoading && this.props.onRatingSelect) {
      this.setState({ selectedRating: rating });
      this.props.onRatingSelect('rating', rating);
    }
  };

  renderWithPopover = (
    i: number,
    labels: Array<{ [key: string]: string }>,
    scaleItem: JSX.Element,
    type?: string,
  ) => (
    <Popover
      key={i}
      content={translateContent(labels[type === 'nps' && i === 10 ? 1 : i])}
      trigger="hover"
      placement="bottom"
    >
      {scaleItem}
    </Popover>
  );

  starIsSelectedByIndex(index: number) {
    if (this.state.selectedRating !== null && !Array.isArray(this.state.selectedRating)) {
      return this.state.selectedRating >= index;
    } else if (this.state.hoverRating !== null) {
      return this.state.hoverRating >= index;
    } else {
      return this.props.rating !== null && !Array.isArray(this.props.rating) && this.props.rating >= index;
    }
  }

  renderStars(): JSX.Element[] {
    const stars = [];
    const { labels, media } = this.props;
    const renderStar = (i: number) => (
      <StarBtn
        key={i}
        onClick={this.handleClick.bind(this, i)}
        onMouseOver={media === 'desktop' ? this.handleMouseOver.bind(this, i) : undefined}
        onMouseOut={media === 'desktop' ? this.handleMouseOut : undefined}
        selected={this.starIsSelectedByIndex(i)}
        $animated={this.state.selectedRating === i}
      >
        <StarIcon name="iconStar" className={this.starIsSelectedByIndex(i) ? 'filled glow' : ''} />
      </StarBtn>
    );
    for (let i = 0; i < 5; i++) {
      if (labels && labels[i] && Object.keys(labels[i]).length && this.props.media === 'desktop') {
        stars.push(this.renderWithPopover(i, labels, renderStar(i)));
      } else {
        stars.push(renderStar(i));
      }
    }
    return stars;
  }

  boxIsSelectedByIndex(index: number) {
    if (this.state.selectedRating !== null) {
      return this.state.selectedRating === index;
    } else if (this.state.hoverRating !== null) {
      return this.state.hoverRating === index;
    } else {
      return this.props.rating === index;
    }
  }

  renderNps(): JSX.Element[] {
    const ratingButtons = [];
    const renderNps = (i: number) => (
      <ScaleMarginContainer key={i} onClick={this.handleClick.bind(this, i)}>
        <ScaleBoxItem
          type={ScaleVisualization.Nps}
          colorNumber={i}
          selected={this.boxIsSelectedByIndex(i)}
          animated={this.state.selectedRating === i}
          scaleSelected={this.props.rating !== null || this.state.selectedRating !== null}
          withHover
          isPreview={this.props.isPreview}
        />
      </ScaleMarginContainer>
    );
    for (let i = 0; i < 11; i++) {
      ratingButtons.push(renderNps(i));
    }
    return ratingButtons;
  }

  renderLabels(isVertical: boolean): JSX.Element[] {
    const colorsArray = orderedColorsSets[this.props.labels.length];
    const enrichedLabels = this.props.labels.map((label, index) => ({
      txt: { ...label },
      color: colorsArray[index],
    }));
    const userHasSelected = this.props.rating !== null || this.state.selectedRating !== null;

    return enrichedLabels.map((label, i) => {
      const currentLabelIsSelected = this.boxIsSelectedByIndex(i);
      return (

        <ScaleMarginContainer
          key={i}
          onClick={this.handleClick.bind(this, i)}
          className={isVertical ? 'vertical' : ''}
        >
          <StyledTextBoxWithTooltip
            text={translateContent(label.txt) || '...'}
            backgroundColor={(!userHasSelected || currentLabelIsSelected) ? orderedColorsSets[enrichedLabels.length][i] : 'rgba(0, 0, 0, 0)'}
            backgroundHoverColor={orderedColorsSets[enrichedLabels.length][i]}
            $cursorPointer
          />
        </ScaleMarginContainer>
      );
    });
  }

  renderMcqLabels(isVertical: boolean): JSX.Element[] {
    const labels = this.props.labels;
    const rating = this.state.selectedRating || this.props.rating || [];
    const mcqRating = Array.isArray(rating) ? rating : [rating];

    return labels.map((label, i) => (
      <ScaleMarginContainer
        key={i}
        onClick={(e) => {
          if (!this.props.isMultipleChoiceEnabled) {
            return this.handleClick(i);
          }
          e.preventDefault();

          if (this.props.isPreview || this.props.saveLoading) {
            return;
          }
          const isSelected = mcqRating.includes(i);
          this.setState({
            selectedRating: isSelected ? mcqRating.filter(r => r !== i) : [...mcqRating, i],
          });
        }}
        className={isVertical ? 'vertical' : ''}
      >
        <ScaleBoxItem
          type={ScaleVisualization.McqOptions}
          vertical={isVertical}
          colorNumber={0}
          isPreview={this.props.isPreview}
          scaleLength={labels.length}
          label={translateContent({ ...label })}
          selected={!mcqRating.length || mcqRating.includes(i)}
          animated={this.state.selectedRating === i}
          scaleSelected={true}
          withHover
          showCheckboxOptions={this.props.isMultipleChoiceEnabled}
          checkboxOptions={
            this.props.isMultipleChoiceEnabled ? { isSelected: mcqRating.includes(i) } : undefined
          }
        />
      </ScaleMarginContainer>
    ));
  }

  render(): JSX.Element {
    let renderScale: JSX.Element[];
    const { t } = this.props;
    const isVertical =
      (this.props.type === ScaleVisualization.Labels || this.props.type === ScaleVisualization.McqOptions) &&
      this.props.labels.length > 4;
    switch (this.props.type) {
      case ScaleVisualization.Stars:
        renderScale = this.renderStars();
        break;
      case ScaleVisualization.Labels:
        renderScale = this.renderLabels(isVertical);
        break;
      case ScaleVisualization.Nps:
        renderScale = this.renderNps();
        break;
      case ScaleVisualization.McqOptions:
        renderScale = this.renderMcqLabels(isVertical);
        break;
      default:
        Error(`This scale type: ${this.props.type} is currently not supported`);
        return (
          <ScaleDisplayContainer
            className={this.props.inContainer ? 'withBackground' : ''}
            $isLast={this.props.isLast}
          />
        );
    }
    return (
      <ScaleDisplayContainer
        className={[this.props.inContainer ? 'withBackground' : '', isVertical ? 'vertical' : ''].join(' ')}
        $isLast={this.props.isLast}
        $isCustomLabelsScale={this.props.isScaleDetails}
      >
        <ScaleBox $type={this.props.type} className={isVertical ? 'vertical' : ''}>
          {renderScale}
          {this.props.type === ScaleVisualization.Nps && this.props.media !== 'phone' && (
            <DisplayNpsValues labels={this.props.labels} />
          )}
        </ScaleBox>
        {this.props.isMultipleChoiceEnabled && this.props.type === ScaleVisualization.McqOptions && (
          <McqOptionsSubmitButtonContainer>
            <McqOptionsSubmitButton
              $buttonShape={ButtonShape.Rectangle}
              $buttonSize={ButtonSizes.Tiny}
              $withBorder
              $highlighted={false}
              data-translation={'x.questions.mcq.selection.submit.button'}
              onClick={() => {
                const rating = this.state.selectedRating || this.props.rating || [];
                const mcqRating = Array.isArray(rating) ? rating : [rating];
                if (this.props.onRatingSelect) {
                  this.props.onRatingSelect('rating', mcqRating);
                }
              }}
            >
              {t('x.questions.mcq.selection.submit.button')}
            </McqOptionsSubmitButton>
          </McqOptionsSubmitButtonContainer>
        )}
      </ScaleDisplayContainer>
    );
  }
}

const mapStateToProps = ({ app }: StoreI): StateProps => ({
  media: app.mediaQuery,
});

export const ScaleComponent = connect(mapStateToProps)(Scale);
export default ScaleComponent;
